import { JulianDate, Color, Resource, Cartesian2, defined, DeveloperError, ConstantProperty, BillboardGraphics, PointGraphics, PolylineGraphics, ArcType, PolygonGraphics, CylinderGraphics, HeightReference, LabelGraphics, LabelStyle, NearFarScalar, VerticalOrigin, ConstantPositionProperty } from 'cesium';
import { getPositionsCenter } from '@cesium-extends/primitive-geojson';

const DefaultColor = "#FC4C02";
const NoDataColor = "#000000";

async function geojsonStatisticQuery(data, params) {
  const { columnName, segNum, maxLen } = params;
  if (!columnName)
    return null;
  const total = data.length;
  const values = data.map((item) => item[columnName]);
  const numValues = values.filter((item) => typeof item === "number").sort((a, b) => a - b);
  const numCount = numValues.length;
  const result = {
    total,
    sections: [],
    values: [],
    min: numValues[0],
    max: numValues[numCount - 1]
  };
  if (maxLen !== void 0) {
    for (let i = 0; i < total; i += 1) {
      const val = values[i];
      if (result.values.length === maxLen)
        break;
      if (!result.values.includes(val)) {
        result.values.push(val);
      }
    }
  }
  if (segNum) {
    const step = numCount / segNum;
    for (let j = 0; j <= numCount; j += step) {
      const val = numValues[Math.floor(j)];
      result.sections.push(val);
    }
    result.sections[segNum] = result.max;
  }
  return result;
}

const DefaultItem = {
  label: "default",
  value: DefaultColor
};
const DefaultItem2 = {
  label: "default",
  value: NoDataColor
};
const single2paintColor = (color, opacity = 1) => {
  if (!color)
    return Color.fromCssColorString(DefaultColor).withAlpha(opacity);
  if (typeof color === "string")
    return Color.fromCssColorString(color).withAlpha(opacity);
  return Color.fromCssColorString(
    `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`
  );
};
const transCustomColorItem = (items, opacity = 1) => {
  return items.map((item) => ({
    label: item.label,
    value: single2paintColor(item.value, opacity)
  }));
};
const section2CustomColor = async (data, config) => {
  const { field, color: colors } = config;
  const sectionType = config["section-type"];
  const result = [];
  const colorNum = colors == null ? void 0 : colors.length;
  if (!data || !colorNum || !field)
    return [DefaultItem];
  if (sectionType) {
    const res = await geojsonStatisticQuery(data, {
      hasMinMax: sectionType === "average",
      columnName: field,
      segNum: sectionType === "natural" ? colorNum : 0
    });
    if (!res)
      return [DefaultItem];
    if (sectionType === "average") {
      const min = res.min;
      const max = res.max;
      if (typeof min !== "number" || typeof max !== "number")
        return [DefaultItem];
      const step = (max - min) / colorNum;
      for (let i = 0; i < colorNum; i += 1) {
        result.push({
          label: [min + step * i, min + step * (i + 1)],
          value: colors[i]
        });
      }
    }
    if (sectionType === "natural") {
      for (let i = 0; i < colorNum; i += 1) {
        const section0 = res.sections[i];
        const section1 = res.sections[i + 1];
        if (typeof section0 !== "number" || typeof section1 !== "number")
          return [DefaultItem];
        result.push({
          label: [section0, section1],
          value: colors[i]
        });
      }
    }
  }
  result.push(DefaultItem2);
  return result;
};
const section2CustomSize = async (data, config) => {
  const { field, num, sizeRange } = config;
  const sectionType = config["section-type"];
  const result = [];
  const DefaultSectionItem = {
    label: "default",
    value: (sizeRange == null ? void 0 : sizeRange[0]) ?? 1
  };
  if (!data || !num || !field || !sizeRange)
    return [DefaultSectionItem];
  if (sectionType) {
    const res = await geojsonStatisticQuery(data, {
      hasMinMax: sectionType === "average",
      columnName: field,
      segNum: sectionType === "natural" ? num : 0
    });
    if (!res)
      return [DefaultSectionItem];
    const min = res.min;
    const max = res.max;
    const sizeStep = (sizeRange[1] - sizeRange[0]) / (num - 1);
    if (sectionType === "average") {
      if (typeof min !== "number" || typeof max !== "number")
        return [DefaultSectionItem];
      const step = (max - min) / num;
      for (let i = 0; i < num; i += 1) {
        result.push({
          label: [min + step * i, min + step * (i + 1)],
          value: sizeRange[0] + sizeStep * i
        });
      }
    }
    if (sectionType === "natural") {
      for (let i = 0; i < num; i += 1) {
        const section0 = res.sections[i];
        const section1 = res.sections[i + 1];
        if (typeof section0 !== "number" || typeof section1 !== "number")
          return [DefaultSectionItem];
        result.push({
          label: [section0, section1],
          value: sizeRange[0] + sizeStep * i
        });
      }
    }
    if (sectionType === "auto") {
      result.push(
        {
          label: min,
          value: sizeRange[0]
        },
        {
          label: max,
          value: sizeRange[1]
        }
      );
    }
  }
  result.push(DefaultSectionItem);
  return result;
};
const value2Custom = async (data, config) => {
  const { field, color: colors } = config;
  const result = [];
  const length = (colors == null ? void 0 : colors.length) ?? 0;
  if (field && colors && length > 0) {
    const res = await geojsonStatisticQuery(data, {
      hasMinMax: false,
      columnName: field,
      maxLen: 1e3
    });
    if (!res)
      return [DefaultItem];
    const { values = [] } = res;
    for (let i = 0; i < values.length; i += 1) {
      result.push({
        label: values[i],
        value: colors[i % length]
      });
    }
    result.push(DefaultItem2);
    return result;
  }
  return [DefaultItem];
};
function custom2value(value, customConfig) {
  const { custom, normalization, default: defaultValue } = customConfig;
  if (!value)
    return defaultValue;
  if (normalization) {
    const { valueRange, normalRange } = normalization;
    const valueRangeSize = valueRange[1] - valueRange[0];
    const normalRangeSize = normalRange[1] - normalRange[0];
    const newValue = (value - valueRange[0]) / valueRangeSize * normalRangeSize + normalRange[0];
    return typeof newValue !== "number" ? defaultValue : newValue;
  }
  if (custom) {
    for (const item of custom) {
      const { label, value: val } = item;
      if (label instanceof Array && label[0] <= value && label[1] >= value) {
        return val;
      }
      if (label === value)
        return val;
    }
  }
  return defaultValue;
}
function getEntityValue(entity, field) {
  var _a;
  if (!field)
    return void 0;
  const property = (_a = entity.properties) == null ? void 0 : _a.getValue(JulianDate.now());
  return property[field] ? String(property[field]) : "";
}
function loadImage$1(url, params) {
  let png = url;
  if (params) {
    const entries = Object.entries(params);
    png = url + "?";
    for (let i = 0; i < entries.length; i += 1) {
      if (i > 0)
        png += "&";
      png += entries[i][0] + "=" + entries[i][1];
    }
  }
  return new Promise((resolve) => {
    const img = new Image();
    img.src = png;
    img.crossOrigin = "Anonymous";
    img.onload = function() {
      resolve(img);
    };
  });
}
const image2canvas$1 = (image, config) => {
  if (!config || !image)
    return void 0;
  const newCanvas = document.createElement("canvas");
  newCanvas.height = config.height;
  newCanvas.width = config.width;
  const context = newCanvas.getContext("2d");
  context == null ? void 0 : context.drawImage(
    image,
    config.x,
    config.y,
    config.width,
    config.height,
    0,
    0,
    config.width,
    config.height
  );
  return newCanvas;
};

const LineConfig2Style = async (data, jsonStyle) => {
  const { type, config } = jsonStyle;
  const style = {
    type: "line",
    paint: {}
  };
  const opacity = config.opacity;
  const commonPaint = {
    width: config["line-width"]
  };
  switch (type) {
    case "single":
      style.paint = {
        material: single2paintColor(config.color, opacity),
        ...commonPaint
      };
      return style;
    case "section":
      const sectionCustom = config.custom ?? await section2CustomColor(data, config);
      style.paint = {
        ...commonPaint
      };
      style.custom = {
        material: {
          field: config.field,
          custom: transCustomColorItem(sectionCustom, opacity),
          default: single2paintColor(DefaultColor, opacity)
        }
      };
      break;
    case "value":
      const valueCustom = config.custom ?? await value2Custom(data, config);
      style.paint = {
        ...commonPaint
      };
      style.custom = {
        material: {
          field: config.field,
          custom: transCustomColorItem(valueCustom, opacity),
          default: single2paintColor(DefaultColor, opacity)
        }
      };
      break;
  }
  return style;
};

const getSpriteJson = (url, params = {}) => {
  return Resource.fetchJson({
    url,
    queryParameters: params
  });
};

const MixConfig2Style = async (data, jsonStyle) => {
  var _a;
  const { config, sprite } = jsonStyle;
  const spriteOption = !sprite ? void 0 : {
    spriteImage: await loadImage$1(
      sprite.url.replace(/(.*)\.(.*)/, "$1.png"),
      sprite.params
    ),
    json: await getSpriteJson(
      sprite.url.replace(/(.*)\.(.*)/, "$1.json"),
      sprite.params
    )
  };
  const imageName = config.markerSymbol;
  const image = spriteOption && imageName ? (_a = image2canvas$1(
    spriteOption.spriteImage,
    spriteOption.json[imageName]
  )) == null ? void 0 : _a.toDataURL() : void 0;
  const style = {
    type: "mix",
    paint: {
      ...config,
      fill: single2paintColor(config.fill),
      markerColor: single2paintColor(config.markerColor),
      markerSymbol: config["label-type"] === "icon" ? image : void 0,
      stroke: single2paintColor(config.stroke),
      clampToGround: true
    }
  };
  return style;
};

const optionsCache = {};
function loadImage(url, params) {
  let png = url;
  if (params) {
    const entries = Object.entries(params);
    png = url + "?";
    for (let i = 0; i < entries.length; i += 1) {
      if (i > 0)
        png += "&";
      png += entries[i][0] + "=" + entries[i][1];
    }
  }
  return new Promise((resolve) => {
    const img = new Image();
    img.src = png;
    img.crossOrigin = "Anonymous";
    img.onload = function() {
      resolve(img);
    };
  });
}
const image2canvas = (image, config, color) => {
  if (!config || !image)
    return void 0;
  const newCanvas = document.createElement("canvas");
  newCanvas.height = config.height;
  newCanvas.width = config.width;
  const context = newCanvas.getContext("2d");
  context == null ? void 0 : context.drawImage(
    image,
    config.x,
    config.y,
    config.width,
    config.height,
    0,
    0,
    config.width,
    config.height
  );
  if (color && context) {
    context.globalCompositeOperation = "source-in";
    context.fillStyle = color;
    context.fillRect(0, 0, config.width, config.height);
  }
  return newCanvas;
};
class SpriteIcon {
  _options;
  _ready;
  readyPromise;
  _cache;
  constructor(sprite) {
    this._ready = false;
    this.readyPromise = this.loadSprite(sprite).then((res) => {
      this._ready = true;
      return res;
    });
    this._cache = {};
  }
  get ready() {
    return this._ready;
  }
  get options() {
    return this._options;
  }
  async loadSprite(sprite) {
    if (!sprite)
      return void 0;
    const key = sprite.url + sprite.params;
    let options;
    if (optionsCache[key])
      options = optionsCache[key];
    else {
      options = {
        spriteImage: await loadImage(
          sprite.url.replace(/(.*)\.(.*)/, "$1.png"),
          sprite.params
        ),
        json: await getSpriteJson(
          sprite.url.replace(/(.*)\.(.*)/, "$1.json"),
          sprite.params
        )
      };
      optionsCache[key] = options;
    }
    this._options = options;
    return this._options;
  }
  getImageByName(name, color = "white") {
    var _a;
    if (!name || !((_a = this._options) == null ? void 0 : _a.spriteImage)) {
      return void 0;
    }
    const key = name + color;
    if (this._cache[key])
      return this._cache[key];
    else {
      const image = image2canvas(
        this._options.spriteImage,
        this._options.json[name],
        color
      );
      this._cache[key] = image;
      return image;
    }
  }
}

const PointConfig2Style = async (data, jsonStyle) => {
  var _a;
  const { type, config } = jsonStyle;
  const style = {
    type: "point",
    cluster: type === "height" ? void 0 : jsonStyle.cluster
  };
  const opacity = config.opacity;
  const commonPaint = {
    outlineWidth: config["circle-stroke-width"],
    outlineColor: single2paintColor(config["circle-stroke-color"], opacity)
  };
  let SpriteIcons;
  if (jsonStyle.sprite) {
    SpriteIcons = new SpriteIcon(jsonStyle.sprite);
    await SpriteIcons.readyPromise;
  }
  const ImageCustom = (_a = config.custom) == null ? void 0 : _a.map((item) => ({
    label: item.label,
    value: SpriteIcons == null ? void 0 : SpriteIcons.getImageByName(item.image)
  }));
  const labelSize = config["label-size"];
  const imageSize = labelSize + (config["circle-stroke-width"] ?? 0) * 2;
  let image = void 0;
  if (config["label-type"] === "icon") {
    const imageName = config["icon-image"];
    image = SpriteIcons == null ? void 0 : SpriteIcons.getImageByName(imageName);
  }
  switch (type) {
    case "single":
      if (config["label-type"] === "icon") {
        const imageName = config["icon-image"];
        const image2 = SpriteIcons == null ? void 0 : SpriteIcons.getImageByName(imageName);
        style.layout = {
          image: image2,
          color: single2paintColor(config.color, opacity),
          width: imageSize,
          height: imageSize
        };
      } else {
        style.paint = {
          color: single2paintColor(config.color, opacity),
          pixelSize: labelSize,
          ...commonPaint
        };
      }
      break;
    case "section":
      const sectionCustom = config.custom ?? await section2CustomColor(data, config);
      style.paint = {
        pixelSize: labelSize,
        ...commonPaint
      };
      style.layout = {
        image,
        width: imageSize,
        height: imageSize
      };
      style.custom = {
        color: {
          field: config.field,
          custom: transCustomColorItem(sectionCustom, opacity),
          default: single2paintColor(DefaultColor, opacity)
        },
        image: {
          field: config.field,
          custom: ImageCustom
        }
      };
      break;
    case "value":
      const valueCustom = config.custom ?? await value2Custom(data, config);
      style.paint = {
        pixelSize: labelSize,
        ...commonPaint
      };
      style.layout = {
        image,
        width: imageSize,
        height: imageSize
      };
      style.custom = {
        color: {
          field: config.field,
          custom: transCustomColorItem(valueCustom, opacity),
          default: single2paintColor(DefaultColor, opacity)
        },
        image: {
          field: config.field,
          custom: ImageCustom
        }
      };
      break;
    case "bubble":
      const sectionType = config["section-type"];
      const bubbleColorCustom = config.custom ?? await section2CustomColor(data, {
        field: config.field,
        color: config.colors,
        "section-type": sectionType
      });
      const colorType = config["fill-type"];
      const strokeSize = config["circle-stroke-width"] ?? 0;
      const color = single2paintColor(config.color, opacity);
      style.paint = {
        ...commonPaint,
        color
      };
      const sizeCustom = await section2CustomSize(data, {
        field: config.field,
        num: config["section-num"],
        "section-type": sectionType,
        sizeRange: labelSize
      });
      const sizeCustomOptions = {
        field: config.field,
        custom: sizeCustom.map((item) => ({
          ...item,
          value: item.value + strokeSize * 2
        }))
      };
      const pixelSizeOptions = {
        field: config.field,
        custom: sizeCustom,
        default: (labelSize == null ? void 0 : labelSize[0]) ?? 5
      };
      if (sectionType === "auto") {
        const valueRange = [sizeCustom[0].label, sizeCustom[1].label];
        sizeCustomOptions.normalization = {
          valueRange,
          normalRange: labelSize.map((size) => size + strokeSize * 2)
        };
        pixelSizeOptions.normalization = {
          valueRange,
          normalRange: labelSize
        };
      }
      style.layout = {
        image,
        color,
        width: imageSize,
        height: imageSize
      };
      style.custom = {
        color: colorType === "multi" && sectionType !== "auto" ? {
          field: config.field,
          custom: transCustomColorItem(bubbleColorCustom, opacity),
          default: single2paintColor(DefaultColor, opacity)
        } : void 0,
        image: colorType === "multi" ? {
          field: config.field,
          custom: ImageCustom
        } : void 0,
        pixelSize: pixelSizeOptions,
        height: sizeCustomOptions,
        width: sizeCustomOptions
      };
      break;
    case "height":
      const heightCustom = config.custom ?? await section2CustomColor(data, config);
      const res = await geojsonStatisticQuery(data, {
        hasMinMax: true,
        columnName: config.field,
        segNum: 0
      });
      style.cylinder = {
        topRadius: config["radius-size"],
        bottomRadius: config["radius-size"]
      };
      style.custom = {
        material: {
          field: config.field,
          custom: transCustomColorItem(heightCustom, opacity),
          default: single2paintColor(DefaultColor, opacity)
        },
        length: {
          field: config.field,
          normalization: {
            valueRange: [(res == null ? void 0 : res.min) ?? 0, (res == null ? void 0 : res.max) ?? 0],
            normalRange: config["height-range"]
          },
          default: 100
        }
      };
      break;
  }
  return style;
};

const PolygonConfig2Style = async (data, jsonStyle) => {
  var _a;
  const { type, config } = jsonStyle;
  const style = {
    type: "polygon",
    paint: {}
  };
  const opacity = config.opacity;
  const commonPaint = {
    outline: true,
    outlineColor: single2paintColor(config["outline-color"], opacity),
    outlineWidth: config["outline-width"]
  };
  switch (type) {
    case "single":
      style.paint = {
        material: single2paintColor(config.color, opacity),
        ...commonPaint
      };
      return style;
    case "section":
      const sectionCustom = config.custom ?? await section2CustomColor(data, config);
      style.paint = {
        ...commonPaint
      };
      style.custom = {
        material: {
          field: config.field,
          custom: transCustomColorItem(sectionCustom, opacity),
          default: single2paintColor(DefaultColor, opacity)
        }
      };
      break;
    case "value":
      const valueCustom = config.custom ?? await value2Custom(data, config);
      style.paint = {
        ...commonPaint
      };
      style.custom = {
        material: {
          field: config.field,
          custom: transCustomColorItem(valueCustom, opacity),
          default: single2paintColor(DefaultColor, opacity)
        }
      };
      break;
    case "height":
      const heightCustom = config.custom ?? await section2CustomColor(data, config);
      const res = await geojsonStatisticQuery(data, {
        hasMinMax: true,
        columnName: config.field,
        segNum: 0
      });
      style.paint = {
        ...commonPaint
      };
      style.custom = {
        material: {
          field: config.field,
          custom: transCustomColorItem(heightCustom, opacity),
          default: single2paintColor(DefaultColor, opacity)
        },
        extrudedHeight: {
          field: config.field,
          normalization: {
            valueRange: [(res == null ? void 0 : res.min) ?? 0, (res == null ? void 0 : res.max) ?? 0],
            normalRange: config["height-range"]
          },
          default: ((_a = config["height-range"]) == null ? void 0 : _a[0]) ?? 0
        }
      };
      break;
  }
  return style;
};

const symbolConfig2Style = (config) => {
  const style = {
    type: "label",
    paint: {
      text: config["text-field"] ? `{${config["text-field"]}}` : void 0,
      font: `${config["text-size"]}px ${config["text-font"]}`,
      fillColor: single2paintColor(config["text-color"]),
      outlineColor: single2paintColor(config["text-halo-color"]),
      outlineWidth: config["text-halo-width"],
      pixelOffset: new Cartesian2(
        config["text-offsetX"] ?? 0,
        config["text-offsetY"] ?? -10
      )
    }
  };
  return style;
};

const RenderConfig2Style = {
  polygon: PolygonConfig2Style,
  line: LineConfig2Style,
  point: PointConfig2Style,
  symbol: symbolConfig2Style,
  mix: MixConfig2Style
};

class BillBuilder {
  _cache;
  constructor() {
    this._cache = {};
  }
  /**
   * Creates a pin with the specified text, color, and size.  The text will be sized to be as large as possible
   * while still being contained completely within the pin.
   *
   * @param {String} text The text to be stamped onto the pin.
   * @param {Color} color The color of the pin.
   * @param {Number} size The size of the pin, in pixels.
   * @returns {HTMLCanvasElement} The canvas element that represents the generated pin.
   */
  fromText(text, color, size) {
    if (!defined(text)) {
      throw new DeveloperError("text is required");
    }
    if (!defined(color)) {
      throw new DeveloperError("color is required");
    }
    if (!defined(size)) {
      throw new DeveloperError("size is required");
    }
    return createPin(void 0, text, color, size, this._cache);
  }
}
const colorScratch = new Color();
function drawArc(context2D, color, size) {
  context2D.save();
  context2D.scale(size / 24, size / 24);
  context2D.fillStyle = color.toCssColorString();
  context2D.strokeStyle = color.brighten(0.6, colorScratch).toCssColorString();
  context2D.arc(12, 12, 6, 0, Math.PI * 2, true);
  context2D.fill();
  context2D.lineWidth = 0.846;
  context2D.stroke();
  context2D.restore();
}
const stringifyScratch = new Array(4);
function createPin(url, label, color, size, cache) {
  stringifyScratch[0] = url;
  stringifyScratch[1] = label;
  stringifyScratch[2] = color;
  stringifyScratch[3] = size;
  const id = JSON.stringify(stringifyScratch);
  const item = cache[id];
  if (defined(item)) {
    return item;
  }
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const context2D = canvas.getContext("2d");
  if (context2D) {
    drawArc(context2D, color, size);
    context2D.font = "16px sans-serif";
    context2D.lineJoin = "round";
    context2D.textAlign = "center";
    context2D.textBaseline = "middle";
    context2D.strokeStyle = "black";
    context2D.lineWidth = 3;
    context2D.strokeText(label, size / 2, size / 2);
    context2D.fillStyle = "white";
    context2D.fillText(label, size / 2, size / 2);
  }
  cache[id] = canvas;
  return canvas;
}

const dataSourceRender = async (dataSource, style) => {
  const entities = dataSource.entities.values;
  const { label, paint, type, custom } = style;
  entities.map((entity) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j;
    const customStyle = {};
    for (const k in custom) {
      if (custom[k]) {
        const config = custom[k];
        config.default = ((_b = (_a = config.custom) == null ? void 0 : _a.find((item) => item.label === "default")) == null ? void 0 : _b.value) ?? config.default;
        const entityVal = getEntityValue(entity, config.field);
        const value = custom2value(entityVal, config);
        customStyle[k] = value;
      }
    }
    switch (type) {
      case "point":
        entity.point = void 0;
        entity.billboard = void 0;
        entity.cylinder = void 0;
        const image = ((_c = style.layout) == null ? void 0 : _c.image) ?? customStyle.image;
        if (image) {
          const billboardStyle = {
            ...style.layout,
            ...customStyle,
            image
          };
          if (billboardStyle.image) {
            entity.billboard = new BillboardGraphics(billboardStyle);
          }
        } else {
          if (style.cylinder) {
            const { cylinder } = style;
            const length = ((customStyle == null ? void 0 : customStyle.length) ?? 1) * 1e3;
            entity.cylinder = new CylinderGraphics({
              ...cylinder,
              ...customStyle,
              length,
              heightReference: HeightReference.CLAMP_TO_GROUND,
              topRadius: ((cylinder == null ? void 0 : cylinder.topRadius) ?? 1) * 1e3,
              bottomRadius: ((cylinder == null ? void 0 : cylinder.bottomRadius) ?? 1) * 1e3
            });
          } else {
            entity.point = new PointGraphics({
              ...paint,
              ...customStyle
            });
          }
        }
        break;
      case "line":
        entity.polygon = void 0;
        entity.polyline = new PolylineGraphics({
          positions: (_d = entity.polyline) == null ? void 0 : _d.positions,
          ...paint,
          ...customStyle,
          clampToGround: true,
          arcType: ArcType.RHUMB
        });
        break;
      case "polygon":
        const height = customStyle == null ? void 0 : customStyle.extrudedHeight;
        entity.polygon = new PolygonGraphics({
          hierarchy: (_e = entity.polygon) == null ? void 0 : _e.hierarchy,
          ...paint,
          ...customStyle,
          extrudedHeight: height ? height * 1e3 : void 0,
          arcType: ArcType.RHUMB
        });
        entity.polyline = new PolylineGraphics({
          positions: (_f = entity.polygon.hierarchy) == null ? void 0 : _f._value.positions,
          width: paint.outlineWidth,
          material: paint.outlineColor,
          clampToGround: true,
          arcType: ArcType.RHUMB
        });
        break;
      case "mix":
        if (entity.billboard) {
          if (paint.markerSymbol) {
            entity.point = void 0;
            entity.billboard.show = new ConstantProperty(true);
            entity.billboard = new BillboardGraphics({
              image: paint.markerSymbol,
              color: paint.markerColor,
              scale: (paint.markerSize ?? 5) / 5
            });
          } else {
            entity.billboard.show = new ConstantProperty(false);
            entity.point = new PointGraphics({
              color: paint.markerColor,
              outlineColor: paint.stroke,
              outlineWidth: paint.strokeWidth,
              pixelSize: paint.markerSize
            });
          }
        }
        entity.polyline = new PolylineGraphics({
          positions: (_g = entity.polyline) == null ? void 0 : _g.positions,
          material: paint.fill,
          width: paint.strokeWidth,
          clampToGround: true,
          arcType: ArcType.RHUMB
        });
        if (entity.polygon) {
          entity.polygon = new PolygonGraphics({
            hierarchy: (_h = entity.polygon) == null ? void 0 : _h.hierarchy,
            material: paint.fill,
            arcType: ArcType.RHUMB
          });
          entity.polyline = new PolylineGraphics({
            positions: (_i = entity.polygon.hierarchy) == null ? void 0 : _i._value.positions,
            width: paint.strokeWidth,
            material: paint.stroke,
            clampToGround: true,
            arcType: ArcType.RHUMB
          });
        }
        break;
    }
    if (label == null ? void 0 : label.paint.text) {
      entity.label = new LabelGraphics({
        font: `bold 20px Arial`,
        outlineWidth: 4,
        style: LabelStyle.FILL_AND_OUTLINE,
        pixelOffset: new Cartesian2(0, -10),
        scale: 1,
        scaleByDistance: new NearFarScalar(1, 0.85, 8e6, 0.75),
        ...label.paint,
        text: (_j = label.paint.text) == null ? void 0 : _j.replace(
          /{([^{}]*)}/g,
          (match, p1) => getEntityValue(entity, p1) ?? ""
        )
      });
    } else
      entity.label = void 0;
  });
  if (style.type === "point") {
    dataSourceCluster(dataSource, style.cluster);
  }
};
const renderGeoJson = async (dataSource, config) => {
  const { type, style } = config;
  if (!type || !style)
    return void 0;
  const data = dataSource.entities.values.map(
    (item) => {
      var _a;
      return (_a = item.properties) == null ? void 0 : _a.getValue(JulianDate.now());
    }
  );
  const entityStyle = await RenderConfig2Style[type](
    data,
    style
  );
  entityStyle.label = style.symbol ? RenderConfig2Style.symbol(style.symbol) : void 0;
  if (entityStyle)
    await dataSourceRender(dataSource, entityStyle);
  return entityStyle;
};
const clusterEvent = (clusteredEntities, cluster, options = {}) => {
  const {
    alpha = 1,
    scale = 1,
    colorBar = [
      "#313695",
      "#4575b4",
      "#74add1",
      "#abd9e9",
      "#e0f3f8",
      "#ffffbf",
      "#fee090",
      "#fdae61",
      "#f46d43",
      "#d73027",
      "#a50026"
    ],
    maxNum = 1e3
  } = options;
  cluster.billboard.show = true;
  cluster.label.show = false;
  cluster.billboard.id = cluster.label.id;
  cluster.billboard.verticalOrigin = VerticalOrigin.CENTER;
  const length = clusteredEntities.length;
  const numSize = length.toString().length;
  const helpNum = Math.pow(10, numSize - 1);
  if (length <= maxNum) {
    const num = numSize > 1 ? ~~(length / helpNum) * helpNum : length;
    cluster.billboard.image = new BillBuilder().fromText(
      num >= 10 ? `${num}+` : String(length),
      Color.fromCssColorString(
        colorBar[Math.floor(length / maxNum * colorBar.length)] ?? colorBar[colorBar.length - 1]
      ),
      80 + length / maxNum * 100
    ).toDataURL();
  } else {
    cluster.billboard.image = new BillBuilder().fromText(
      `${length}`,
      Color.fromCssColorString(colorBar[colorBar.length - 1]),
      180
    ).toDataURL();
  }
  cluster.billboard.color.alpha = alpha;
  cluster.billboard.scale = scale;
};
function dataSourceCluster(dataSource, options = {}) {
  const { enable = false, pixelRange = 80, minimumClusterSize = 2 } = options;
  dataSource.clustering.enabled = enable;
  dataSource.clustering.pixelRange = pixelRange;
  dataSource.clustering.minimumClusterSize = minimumClusterSize;
  dataSource.clustering.clusterEvent.removeEventListener(clusterEvent);
  if (enable)
    dataSource.clustering.clusterEvent.addEventListener(
      (clusteredEntities, cluster) => clusterEvent(clusteredEntities, cluster, options)
    );
}
function updateEntityPosition(entity) {
  var _a, _b, _c;
  if (entity.polygon) {
    const center = getPositionsCenter(
      (_b = (_a = entity.polygon) == null ? void 0 : _a.hierarchy) == null ? void 0 : _b.getValue(JulianDate.now()).positions
    ).cartesian3;
    entity.position = new ConstantPositionProperty(center);
    return;
  }
  if (entity.polyline) {
    const positions = (_c = entity.polyline.positions) == null ? void 0 : _c.getValue(JulianDate.now());
    const center = positions[Math.floor(positions.length / 2)];
    entity.position = new ConstantPositionProperty(center);
    return;
  }
}
function updateDataSourcePosition(dataSource) {
  const entities = dataSource.entities.values;
  for (let i = 0; i < entities.length; i += 1) {
    const entity = entities[i];
    updateEntityPosition(entity);
  }
}

const primitiveGeoJsonRender = async (primitiveLayer, style) => {
  const featureItems = [...primitiveLayer.featureItems];
  primitiveLayer.removeAllPrimitive();
  const { label, paint, type, custom } = style;
  let depthTest = void 0;
  featureItems.map((feature) => {
    var _a, _b, _c, _d, _e, _f;
    const customStyle = {};
    for (const k in custom) {
      if (custom[k]) {
        const config = custom[k];
        config.default = ((_b = (_a = config.custom) == null ? void 0 : _a.find((item) => item.label === "default")) == null ? void 0 : _b.value) ?? config.default;
        const featureVal = (_c = feature.properties) == null ? void 0 : _c[config.field ?? ""];
        const value = custom2value(featureVal, config);
        customStyle[k] = value;
      }
    }
    const height = customStyle == null ? void 0 : customStyle.extrudedHeight;
    const extrudedHeight = height ? height * 1e3 : void 0;
    if (depthTest === void 0 && height)
      depthTest = true;
    switch (type) {
      case "point":
        const image = ((_d = style.layout) == null ? void 0 : _d.image) ?? customStyle.image;
        if (image) {
          primitiveLayer.addBillboard({
            ...feature,
            style: {
              ...style.layout,
              ...customStyle,
              image
            }
          });
        }
        if (!((_e = style.layout) == null ? void 0 : _e.image)) {
          if (style.cylinder) {
            const { cylinder } = style;
            const cylinderStyle = {
              ...cylinder,
              ...customStyle
            };
            primitiveLayer.addCircle({
              ...feature,
              style: {
                color: cylinderStyle.material,
                radius: (cylinderStyle.bottomRadius ?? 1) * 1e3,
                extrudedHeight: (cylinderStyle.length ?? 0) * 1e3
              }
            });
          } else {
            primitiveLayer.addPoint({
              ...feature,
              style: {
                ...paint,
                ...customStyle
              }
            });
          }
        }
        break;
      case "line":
        primitiveLayer.addPolyline({
          ...feature,
          style: {
            ...paint,
            ...customStyle,
            extrudedHeight
          }
        });
        break;
      case "polygon":
        primitiveLayer.addPolygon({
          ...feature,
          style: {
            ...paint,
            ...customStyle,
            extrudedHeight
          }
        });
        break;
      case "mix":
        if (feature.type === "Point" || feature.type === "Billboard") {
          if (paint.markerSymbol) {
            primitiveLayer.addBillboard({
              ...feature,
              style: {
                image: paint.markerSymbol,
                color: paint.markerColor,
                scale: (paint.markerSize ?? 5) / 5
              }
            });
          } else {
            primitiveLayer.addPoint({
              ...feature,
              style: {
                color: paint.markerColor,
                outlineColor: paint.stroke,
                outlineWidth: paint.strokeWidth,
                pixelSize: paint.markerSize
              }
            });
          }
        }
        if (feature.type === "Polygon") {
          primitiveLayer.addPolygon({
            ...feature,
            style: {
              material: paint.fill,
              outlineColor: paint.stroke,
              outlineWidth: paint.strokeWidth
            }
          });
        }
        if (feature.type === "Polyline") {
          primitiveLayer.addPolyline({
            ...feature,
            style: {
              material: paint.fill,
              width: paint.strokeWidth
            }
          });
        }
        break;
    }
    if ((label == null ? void 0 : label.paint.text) && feature.center) {
      primitiveLayer.addLabel({
        type: "Label",
        position: feature.center.cartesian3,
        style: {
          font: `bold 20px Arial`,
          outlineWidth: 4,
          style: LabelStyle.FILL_AND_OUTLINE,
          pixelOffset: new Cartesian2(0, -10),
          scale: 1,
          scaleByDistance: new NearFarScalar(1, 0.85, 8e6, 0.75),
          ...label.paint,
          text: (_f = label.paint.text) == null ? void 0 : _f.replace(
            /{([^{}]*)}/g,
            (match, p1) => {
              var _a2;
              return ((_a2 = feature.properties) == null ? void 0 : _a2[p1]) ?? "";
            }
          )
        }
      });
    }
  });
  primitiveLayer.reloadPrimitive(depthTest);
};
const renderPrimitiveGeoJson = async (primitiveLayer, config) => {
  const { type, style } = config;
  if (!type || !style)
    return;
  const data = primitiveLayer.featureItems.map((feature) => feature.properties).filter((item) => item !== void 0);
  const entityStyle = await RenderConfig2Style[type](
    data,
    style
  );
  entityStyle.label = style.symbol ? RenderConfig2Style.symbol(style.symbol) : void 0;
  if (entityStyle)
    await primitiveGeoJsonRender(primitiveLayer, entityStyle);
  return entityStyle;
};

export { dataSourceRender, primitiveGeoJsonRender, renderGeoJson, renderPrimitiveGeoJson, updateDataSourcePosition };
